import { ArpMedia, ArpMediaType, PaginatedResponse, QueryParams } from '@arpeggi/shared'
import axios from 'axios'
import { IAnyModelType, Instance, SnapshotIn, types, unprotect } from 'mobx-state-tree'
import qs from 'qs'
import { ForDirectExtend } from '../../../features/mobx'
import { GetArpMediaParams } from '../../api/v2/ArpMediaAPI'
import { log, LogCategories } from '../../utils/logger'

interface IRegistryOptions<Type> {
  baseURL: string
  primaryKey: keyof Type
  type?: ArpMediaType
}

/**
 *
 * @param type type of the model that will be served on this registry
 * @param params some parameters to set, like baseURL for the registry API endpoint and primary key in dynamodb (basically an index)
 * @returns a new model with types that can be instantiated
 */
export const createRegistry = <T extends IAnyModelType>(
  type: T,
  params: IRegistryOptions<SnapshotIn<T>>
) => {
  const typeFilter = params.type || ArpMediaType.SONG
  type IT = Instance<T>
  type ST = SnapshotIn<T>
  const _MyRegistry = types
    .model({
      cache: types.map(types.maybeNull(type)),
      status: types.enumeration(['loading', 'ready', 'error']),
      indexes: types.array(types.string)
    })
    .actions((self) => {
      let nextPage = null

      function afterCreate() {
        unprotect(self)
      }
      const promises = new Map<string, Promise<any>>()

      const { baseURL } = params

      const instance = axios.create({
        baseURL,
        withCredentials: false,
        xsrfCookieName: null,
        paramsSerializer: (params) => {
          return qs.stringify(params)
        }
      })
      function setReady() {
        self.status = 'ready'
      }

      async function _fetchByIndex(index: string): Promise<IT | undefined> {
        try {
          const resp = await instance.get<ST>(index, {
            params: {
              parts: ['subComponents']
            }
          })
          self.cache.set(index, resp.data)

          if (self.indexes.indexOf(index.toString()) < 0) {
            self.indexes.push(index.toString())
          }
        } catch (err) {
          console.warn(`[registry]`, index, err)
          self.cache.delete(index)
        }
        return self.cache.get(index)
      }

      async function _fetchAll(): Promise<IT[]> {
        self.status = 'loading'
        const requestParams: GetArpMediaParams = {
          [QueryParams.PER_PAGE]: 10000,
          [QueryParams.ORDER]: 'desc',
          type: [typeFilter]
        }
        const resp = await instance.get<PaginatedResponse<ArpMedia>>('/', { params: requestParams })
        if (!resp.data) {
          self.status = 'error'
          throw new Error('[registry] error fetching all')
        }
        resp.data.data.forEach((e) => {
          try {
            const index = e.id.toString()
            self.cache.set(index, e)
            self.indexes.push(index)
          } catch (err) {
            console.warn('[registry] item casting to model error', err)
            return
          }
        })
        return Array.from(self.cache.values())
      }

      async function getAllAsync(): Promise<IT[]> {
        if (!promises.has('all')) {
          promises.set('all', _fetchAll())
        }
        const data = await promises.get('all')
        promises.delete('all')
        self.status = 'ready'
        return data
      }

      /**
       * Gets the item from the registry at the given index asynchronously
       * @param index
       * @returns promise of the item or undefined if it doesn't exist or there was an error
       */
      async function getByIndexAsync(index: string | number): Promise<IT | null> {
        if ((typeof index === 'number' && index < 0) || index === '-1') {
          log.warn(
            { category: LogCategories.REGISTRY, data: { index } },
            'Warning: requested sample is invalid.'
          )
          return null
        }
        const stringIndex = index.toString()
        const item = self.cache.get(stringIndex)
        if (item && item !== 'loading') return item
        if (promises.has('all')) await promises.get('all')
        if (!self.cache.has(stringIndex)) {
          if (!promises.has(stringIndex)) promises.set(stringIndex, _fetchByIndex(stringIndex))
          await promises.get(stringIndex)
        }
        return self.cache.get(stringIndex)
      }

      /**
       * Gets a batch of (most likely) uncached items (useful for infinite scroll pages)
       */
      async function fetchNewBatchAsync(
        {
          limit = 20,
          params,
          resetLastEvaluated
        }: {
          limit?: number
          params?: GetArpMediaParams
          resetLastEvaluated?: boolean
        } = { limit: 20 }
      ): Promise<Array<IT | null>> {
        if (resetLastEvaluated) nextPage = 1

        const requestParams: GetArpMediaParams = {
          [QueryParams.PARTS]: ['subComponents'],
          [QueryParams.PAGE]: nextPage || 1,
          [QueryParams.PER_PAGE]: limit,
          [QueryParams.ORDER]: 'desc',
          shadowBlocklist: true,
          type: [typeFilter]
        }

        const resp = await instance.get<PaginatedResponse<ArpMedia>>('/', {
          params: { ...requestParams, ...params }
        })
        const { currentPage, lastPage } = resp.data.meta
        nextPage = currentPage + 1
        const media = resp.data.data
        const indexes = media.map((m) => {
          const index = m.id.toString()
          self.cache.set(index, m)
          return self.cache.get(index)
        })
        return indexes
      }

      /**
       * Gets the item from the registry at the given index asynchronously,
       * force pulling it from the database regardless of whether it has been cached
       * @param index
       * @returns promise of the item or undefined if it doesn't exist or there was an error
       */
      async function forceRefreshIndex(index: string | number): Promise<IT | null> {
        return await _fetchByIndex(index.toString())
      }

      async function getByIndexBulk(indexes: string[] | number[]): Promise<Array<IT | null>> {
        return await Promise.all(indexes.map((index) => getByIndexAsync(index)))
      }

      function getRandomIndex(): string {
        return self.indexes[Math.floor(Math.random() * self.indexes.length)]
      }

      return {
        afterCreate,
        setReady,
        getByIndexAsync,
        getAllAsync,
        getByIndexBulk,
        forceRefreshIndex,
        fetchNewBatchAsync,
        getRandomIndex
      }
    })
    .views((self) => ({
      /**
       * Gets the cached object by either the integer index (for ARP media) or the string
       * key (for ETH addresses).
       *
       * @return IMPORTANT: the item, or undefined if the item isn't immediately avialable.
       */
      getByIndexReactive(index: string | number): IT | undefined {
        if (index === undefined) return null
        const stringIndex = index.toString()
        if (!self.cache.has(stringIndex)) {
          self.getByIndexAsync(stringIndex)
        }
        return self.cache.get(stringIndex)
      },
      getAll(): IT[] {
        return Array.from(self.cache.values())
      },
      get isReady(): boolean {
        return self.status === 'ready'
      }
    }))

  interface MyRegistryType extends ForDirectExtend<typeof _MyRegistry> {}
  const MyRegistry: MyRegistryType = _MyRegistry

  return MyRegistry
}
